import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Title3,
  Combobox,
  Option,
  ProgressBar,
  Input,
  Badge,
  shorthands,
} from "@fluentui/react-components";
import {
  ArrowSync24Regular,
  Search24Regular,
  Checkmark16Regular,
  Warning16Regular,
  Dismiss16Regular,
  ArrowUndo16Regular,
} from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import {
  getXeroAccounts,
  syncXeroAccounts,
  updateAccountMapping,
  updateBdoOverride,
  XeroAccount,
} from "../../services/xero-integration.service";
import { useTypographyStyles } from "../../styles/typography.styles";
import {
  reportCodes,
  categoryNames,
  getReportCodeDisplay,
} from "./report-codes";

// Maximum options to render at once for performance
const MAX_VISIBLE_OPTIONS = 50;

const useStyles = makeStyles({
  container: {
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  progressSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  progressInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  filterSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  searchInput: {
    flexGrow: 1,
    minWidth: "200px",
    maxWidth: "300px",
  },
  filterBadge: {
    cursor: "pointer",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    position: "sticky",
    top: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    zIndex: 1,
  },
  th: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    textAlign: "left",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
  },
  tr: {
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  trUnmapped: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ":hover": {
      backgroundColor: tokens.colorPaletteYellowBackground2,
    },
  },
  td: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
    fontSize: tokens.fontSizeBase300,
  },
  accountCode: {
    fontFamily: tokens.fontFamilyMonospace,
    fontWeight: tokens.fontWeightMedium,
  },
  statusIcon: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  combobox: {
    minWidth: "280px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding(tokens.spacingVerticalXXXL),
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalL),
    textAlign: "center",
    color: tokens.colorPaletteRedForeground1,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalXXXL),
    color: tokens.colorNeutralForeground3,
  },
  optionCategory: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  optionContent: {
    display: "flex",
    flexDirection: "column",
  },
  optionName: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase200,
  },
  optionCode: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground3,
  },
  moreResults: {
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  clearOption: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  clearOptionText: {
    fontStyle: "italic",
  },
  thStatus: {
    width: "40px",
  },
  thCode: {
    width: "80px",
  },
  thType: {
    width: "100px",
  },
  thReportCode: {
    width: "320px",
  },
  thBdoCode: {
    width: "160px",
  },
  bdoCodeCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  bdoCodeInput: {
    width: "80px",
    fontFamily: tokens.fontFamilyMonospace,
  },
  resetButton: {
    minWidth: "auto",
    padding: "2px",
  },
});

/**
 * Searchable report code combobox with optimized rendering
 */
interface ReportCodeComboboxProps {
  value: string | null;
  onChange: (code: string | null) => void;
  className?: string;
}

const ReportCodeCombobox: React.FC<ReportCodeComboboxProps> = ({
  value,
  onChange,
  className,
}) => {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState("");

  // Filter and limit options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) {
      // No search - return first MAX_VISIBLE_OPTIONS items grouped by category
      return {
        items: reportCodes.slice(0, MAX_VISIBLE_OPTIONS),
        hasMore: reportCodes.length > MAX_VISIBLE_OPTIONS,
        totalCount: reportCodes.length,
      };
    }

    const query = searchQuery.toLowerCase();
    const matches = reportCodes.filter(
      (rc) =>
        rc.code.toLowerCase().includes(query) ||
        rc.name.toLowerCase().includes(query)
    );

    return {
      items: matches.slice(0, MAX_VISIBLE_OPTIONS),
      hasMore: matches.length > MAX_VISIBLE_OPTIONS,
      totalCount: matches.length,
    };
  }, [searchQuery]);

  // Group filtered options by category for better UX
  const groupedOptions = useMemo(() => {
    const groups: Record<string, typeof reportCodes> = {};
    for (const rc of filteredOptions.items) {
      if (!groups[rc.category]) {
        groups[rc.category] = [];
      }
      groups[rc.category].push(rc);
    }
    return groups;
  }, [filteredOptions.items]);

  const handleOptionSelect = useCallback(
    (_: unknown, data: { optionValue?: string }) => {
      const selectedCode = data.optionValue || null;
      // Reset search when selection is made
      setSearchQuery("");
      onChange(selectedCode === "" ? null : selectedCode);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(ev.target.value);
    },
    []
  );

  const displayValue = value ? getReportCodeDisplay(value) : "";

  return (
    <Combobox
      className={`${styles.combobox} ${className || ""}`}
      placeholder="Search report codes..."
      value={searchQuery || displayValue}
      selectedOptions={value ? [value] : []}
      onOptionSelect={handleOptionSelect}
      onChange={handleInputChange}
      freeform
      autoComplete="off"
    >
      {/* Clear option */}
      {value && (
        <Option value="" text="Clear mapping">
          <div className={styles.clearOption}>
            <Dismiss16Regular />
            <Text className={styles.clearOptionText}>Clear mapping</Text>
          </div>
        </Option>
      )}

      {/* Grouped options */}
      {Object.entries(groupedOptions).map(([category, codes]) => (
        <React.Fragment key={category}>
          <Option
            value={`header-${category}`}
            text={categoryNames[category] || category}
            disabled
          >
            <Text className={styles.optionCategory}>
              {categoryNames[category] || category}
            </Text>
          </Option>
          {codes.map((rc) => (
            <Option key={rc.code} value={rc.code} text={`${rc.code} - ${rc.name}`}>
              <div className={styles.optionContent}>
                <span className={styles.optionName}>{rc.name}</span>
                <span className={styles.optionCode}>{rc.code}</span>
              </div>
            </Option>
          ))}
        </React.Fragment>
      ))}

      {/* More results indicator */}
      {filteredOptions.hasMore && (
        <Option value="more-results" disabled text="More results available">
          <Text className={styles.moreResults}>
            Showing {filteredOptions.items.length} of {filteredOptions.totalCount} -
            type to narrow results
          </Text>
        </Option>
      )}

      {/* No results */}
      {filteredOptions.items.length === 0 && searchQuery && (
        <Option value="no-results" disabled text="No results">
          <Text className={styles.moreResults}>
            No report codes match "{searchQuery}"
          </Text>
        </Option>
      )}
    </Combobox>
  );
};

/**
 * Maps Xero accounts to BDO workpaper codes. Supports sync-from-Xero,
 * per-account report-code mapping, and per-account BDO-code overrides.
 */
const AccountMappings: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { workbookSetup } = useWorkflowIntelligence();
  const xeroTenantId = workbookSetup?.xeroTenantId;

  // State
  const [accounts, setAccounts] = useState<XeroAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "unmapped">("all");

  // Load accounts on mount
  useEffect(() => {
    const loadAccounts = async () => {
      if (!xeroTenantId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await getXeroAccounts(xeroTenantId);
        setAccounts(data);
      } catch (err) {
        console.error("Error loading accounts:", err);
        setError("Failed to load accounts");
      } finally {
        setIsLoading(false);
      }
    };

    loadAccounts();
  }, [xeroTenantId]);

  // Statistics
  const stats = useMemo(() => {
    const mapped = accounts.filter((a) => a.mappingStatus === "mapped").length;
    const unmapped = accounts.filter(
      (a) => a.mappingStatus === "unmapped"
    ).length;
    const total = accounts.length;
    const progress = total > 0 ? mapped / total : 0;
    return { mapped, unmapped, total, progress };
  }, [accounts]);

  // Filtered accounts
  const filteredAccounts = useMemo(() => {
    let result = [...accounts];

    // Search filter
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.code?.toLowerCase().includes(lowerSearch) ||
          a.name.toLowerCase().includes(lowerSearch) ||
          a.type?.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (filterStatus === "unmapped") {
      result = result.filter((a) => a.mappingStatus === "unmapped");
    }

    return result;
  }, [accounts, search, filterStatus]);

  // Handlers
  const handleSync = async () => {
    if (!xeroTenantId || isSyncing) return;

    setIsSyncing(true);
    try {
      const data = await syncXeroAccounts(xeroTenantId);
      setAccounts(data);
    } catch (err) {
      console.error("Error syncing accounts:", err);
      setError("Failed to sync accounts from Xero");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleMappingChange = useCallback(
    async (accountId: number, reportCode: string | null) => {
      try {
        await updateAccountMapping(accountId, reportCode);
        // Update local state
        setAccounts((prev) =>
          prev.map((a) =>
            a.id === accountId
              ? {
                  ...a,
                  reportCode,
                  mappingStatus: reportCode ? "mapped" : "unmapped",
                }
              : a
          )
        );
      } catch (err) {
        console.error("Error updating mapping:", err);
      }
    },
    []
  );

  const handleBdoOverrideChange = useCallback(
    async (accountId: number, bdoOverride: string | null) => {
      try {
        await updateBdoOverride(accountId, bdoOverride);
        // Reload accounts to get updated bdoWorkpaperCode from view
        if (xeroTenantId) {
          const data = await getXeroAccounts(xeroTenantId);
          setAccounts(data);
        }
      } catch (err) {
        console.error("Error updating BDO override:", err);
      }
    },
    [xeroTenantId]
  );

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Loading accounts..." />
        </div>
      </div>
    );
  }

  // Render error state
  if (error && accounts.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text weight="semibold">Error loading accounts</Text>
          <Text>{error}</Text>
          {xeroTenantId && (
            <Button appearance="primary" onClick={handleSync}>
              Try syncing from Xero
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Render empty state
  if (accounts.length === 0 && !xeroTenantId) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title3>Account Mappings</Title3>
        </div>
        <div className={styles.emptyState}>
          <Text weight="semibold">No Xero organisation connected</Text>
          <Text>
            Connect a Xero organisation in the Setup page to sync accounts.
          </Text>
        </div>
      </div>
    );
  }

  if (accounts.length === 0 && xeroTenantId) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title3>Account Mappings</Title3>
          <Button
            appearance="primary"
            icon={<ArrowSync24Regular />}
            onClick={handleSync}
            disabled={isSyncing}
          >
            {isSyncing ? "Syncing..." : "Sync from Xero"}
          </Button>
        </div>
        <div className={styles.emptyState}>
          <Text weight="semibold">No accounts found</Text>
          <Text>Click "Sync from Xero" to fetch accounts from your Xero organisation.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title3>Account Mappings</Title3>
        <Button
          appearance="secondary"
          icon={<ArrowSync24Regular />}
          onClick={handleSync}
          disabled={isSyncing}
        >
          {isSyncing ? "Syncing..." : "Sync"}
        </Button>
      </div>

      {/* Progress Section */}
      <div className={styles.progressSection}>
        <div className={styles.progressInfo}>
          <Text className={typography.bodySmall}>
            {stats.mapped} of {stats.total} accounts mapped
          </Text>
          <Text className={typography.bodySmall}>
            {Math.round(stats.progress * 100)}%
          </Text>
        </div>
        <ProgressBar value={stats.progress} thickness="medium" />
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <Input
          className={styles.searchInput}
          placeholder="Search accounts..."
          contentBefore={<Search24Regular />}
          value={search}
          onChange={(_, data) => setSearch(data.value)}
        />
        <Badge
          appearance={filterStatus === "all" ? "filled" : "outline"}
          color="informative"
          className={styles.filterBadge}
          onClick={() => setFilterStatus("all")}
        >
          All ({stats.total})
        </Badge>
        <Badge
          appearance={filterStatus === "unmapped" ? "filled" : "outline"}
          color="warning"
          className={styles.filterBadge}
          onClick={() => setFilterStatus("unmapped")}
        >
          Unmapped ({stats.unmapped})
        </Badge>
        <Text className={typography.caption}>
          Showing {filteredAccounts.length} accounts
        </Text>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={`${styles.th} ${styles.thStatus}`} aria-label="Status"></th>
              <th className={`${styles.th} ${styles.thCode}`}>Code</th>
              <th className={styles.th}>Name</th>
              <th className={`${styles.th} ${styles.thType}`}>Type</th>
              <th className={`${styles.th} ${styles.thReportCode}`}>Report Code</th>
              <th className={`${styles.th} ${styles.thBdoCode}`}>Workpaper</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map((account) => (
              <tr
                key={account.id}
                className={`${styles.tr} ${
                  account.mappingStatus === "unmapped" ? styles.trUnmapped : ""
                }`}
              >
                <td className={styles.td}>
                  <div className={styles.statusIcon}>
                    {account.mappingStatus === "mapped" ? (
                      <Checkmark16Regular primaryFill={tokens.colorPaletteGreenForeground1} />
                    ) : (
                      <Warning16Regular primaryFill={tokens.colorPaletteYellowForeground1} />
                    )}
                  </div>
                </td>
                <td className={`${styles.td} ${styles.accountCode}`}>
                  {account.code || "-"}
                </td>
                <td className={styles.td}>{account.name}</td>
                <td className={styles.td}>
                  <Text className={typography.caption}>{account.type || "-"}</Text>
                </td>
                <td className={styles.td}>
                  <ReportCodeCombobox
                    value={account.reportCode}
                    onChange={(code) => handleMappingChange(account.id, code)}
                  />
                </td>
                <td className={styles.td}>
                  <div className={styles.bdoCodeCell}>
                    <Input
                      key={`${account.id}-${account.bdoWorkpaperCode}`}
                      className={styles.bdoCodeInput}
                      size="small"
                      defaultValue={account.bdoWorkpaperCode || ""}
                      placeholder="-"
                      onBlur={(e) => {
                        const newValue = e.target.value.trim();
                        // Only update if the value changed
                        if (newValue !== (account.bdoWorkpaperCode || "")) {
                          handleBdoOverrideChange(account.id, newValue || null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                    />
                    {account.bdoWorkpaperCodeOverride && (
                      <Button
                        appearance="subtle"
                        size="small"
                        className={styles.resetButton}
                        icon={<ArrowUndo16Regular />}
                        title="Reset to default mapping"
                        onClick={() => handleBdoOverrideChange(account.id, null)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountMappings;
